import React, { useState, useEffect } from 'react';
import axios from 'axios';
import VendorLedgerRow from './VendorLedgerRow';
import VendorLedgerFilters from './VendorLedgerFilters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmt = (n) => '\u20B9' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

// BATCH 5 · Session 4 — Vendor Booking Ledger: reconcile a vendor's real
// settled bookings (confirmed/amended, never provisional-rate) against their
// own statement, with an audit toggle for cancelled bookings.
const AccountingVendorLedger = ({ vendors = [] }) => {
  const [vendorId, setVendorId] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [includeCancelled, setIncludeCancelled] = useState(false);
  const [summary, setSummary] = useState({ count: 0, totalCostInr: 0, totalFinalPrice: 0, totalRevenue: 0 });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const params = { includeCancelled };
      if (vendorId) params.vendorId = vendorId;
      if (startDate) params.startDate = startDate;
      if (endDate) params.endDate = endDate;
      const res = await axios.get(`${BACKEND_URL}/api/accounting/vendor-ledger`, { params });
      setRows(res.data.rows || []);
      setSummary(res.data.summary || {});
    } catch (err) {
      // silent — filters area shows an empty table on failure
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const rowList = [];
  for (let idx = 0; idx < rows.length; idx++) {
    rowList.push(<VendorLedgerRow key={idx} row={rows[idx]} idx={idx} />);
  }

  return (
    <div className="space-y-5" data-testid="vendor-ledger-page">
      <VendorLedgerFilters
        vendors={vendors}
        vendorId={vendorId}
        setVendorId={setVendorId}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        includeCancelled={includeCancelled}
        setIncludeCancelled={setIncludeCancelled}
        onApply={load}
        loading={loading}
      />

      <div className="grid grid-cols-3 gap-4">
        <div className="p-4 rounded-xl border border-slate-200 bg-white">
          <p className="text-xs text-slate-500">Bookings</p>
          <p className="text-2xl font-bold text-slate-800" data-testid="ledger-summary-count">{summary.count}</p>
        </div>
        <div className="p-4 rounded-xl border border-slate-200 bg-white">
          <p className="text-xs text-slate-500">{'Total Vendor Cost (\u20B9)'}</p>
          <p className="text-2xl font-bold text-slate-800" data-testid="ledger-summary-cost">{fmt(summary.totalCostInr)}</p>
        </div>
        <div className="p-4 rounded-xl border border-slate-200 bg-white">
          <p className="text-xs text-slate-500">{'Total Agency Revenue (\u20B9)'}</p>
          <p className="text-2xl font-bold text-teal-700" data-testid="ledger-summary-revenue">{fmt(summary.totalRevenue)}</p>
        </div>
      </div>

      <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
        <table className="w-full text-sm" data-testid="ledger-table">
          <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
            <tr>
              <th className="text-left px-3 py-2 font-semibold">Vendor</th>
              <th className="text-left px-3 py-2 font-semibold">Trip / Guest</th>
              <th className="text-left px-3 py-2 font-semibold">Category</th>
              <th className="text-left px-3 py-2 font-semibold">Booking Ref</th>
              <th className="text-left px-3 py-2 font-semibold">Vendor Booking Status</th>
              <th className="text-right px-3 py-2 font-semibold">{'Cost (\u20B9)'}</th>
              <th className="text-right px-3 py-2 font-semibold">{'Revenue (\u20B9)'}</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={7} className="text-center text-slate-400 italic py-6 text-xs">No bookings match these filters.</td></tr>
            ) : rowList}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AccountingVendorLedger;
